import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { calculateTooltipPosition } from '../utils/tooltipUtils';

/** Cleared item-tooltip state. Named so the four places that reset it agree. */
const NO_TOOLTIP = { item: null, anchor: null, interactive: false, stacked: false, onOpenItem: null };

/**
 * Item/set tooltip state machine for the Generator page.
 *
 * Item tooltips take priority over set tooltips: showing an item tooltip
 * stashes any active set tooltip as "pending" and restores it once the item
 * tooltip hides. Also owns the two housekeeping effects that hide tooltips
 * when pointer hover isn't available (touch) or when the anchor element has
 * been unmounted from under an open tooltip.
 *
 * A tooltip can be *interactive* (the ten main build slots): it accepts the
 * pointer, so leaving the slot towards it must not hide it, and leaving it
 * towards the slot must not either. Both directions are decided from
 * `relatedTarget` — the element the pointer is entering — which is exact and
 * needs no grace timer. The visible gap between slot and tooltip is spanned by
 * a transparent hit-strip on the panel that carries it (HoverPanel.css), so the
 * pointer never crosses a third element on the way and `relatedTarget` names the
 * panel even mid-gap.
 *
 * Only reads `build` for the weapon-maging lookup on the slot tooltip;
 * otherwise fully self-contained (see docs/generator-slicing.md cluster G).
 */
export function useSlotTooltips({ build, hasPointerHover }) {
    const [tooltip, setTooltip] = useState(NO_TOOLTIP);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    // The rendered panel, so a slot's mouseleave can tell "the pointer went into
    // the tooltip" from "the pointer went away".
    const tooltipElRef = useRef(null);

    const slotTooltipWeaponMaging = useMemo(() => {
        if (!tooltip.item || !build) return null;
        const w = build.getWeapon();
        if (!w || String(w.id) !== String(tooltip.item.id)) return null;
        return build.getWeaponMaging?.() ?? null;
    }, [tooltip.item, build]);

    // Set tooltip state
    const [setTooltipData, setSetTooltipData] = useState({ set: null, anchor: null });
    const [setTooltipPosition, setSetTooltipPosition] = useState({ x: 0, y: 0 });

    // Store pending set tooltip data when item tooltip is active
    const pendingSetTooltipRef = useRef({ set: null, anchor: null, position: null });
    // Ref to track current set tooltip state (to avoid dependency issues in callbacks)
    const setTooltipDataRef = useRef({ set: null, anchor: null });
    const setTooltipPositionRef = useRef({ x: 0, y: 0 });

    useEffect(() => {
        if (hasPointerHover) return;
        setTooltip(NO_TOOLTIP);
        setSetTooltipData({ set: null, anchor: null });
        setTooltipDataRef.current = { set: null, anchor: null };
        pendingSetTooltipRef.current = { set: null, anchor: null, position: null };
    }, [hasPointerHover]);

    useEffect(() => {
        if (tooltip.anchor && !tooltip.anchor.isConnected) {
            setTooltip(NO_TOOLTIP);
        }
        if (setTooltipData.anchor && !setTooltipData.anchor.isConnected) {
            setSetTooltipData({ set: null, anchor: null });
            setTooltipDataRef.current = { set: null, anchor: null };
        }
    }, [tooltip, setTooltipData]);

    // Tooltip handlers
    const handleTooltipShow = useCallback((item, event, options = {}) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const interactive = !!options.interactive;

        // Store current set tooltip data if it exists, then hide it (item tooltip has priority)
        if (setTooltipDataRef.current.set) {
            pendingSetTooltipRef.current = {
                set: setTooltipDataRef.current.set,
                anchor: setTooltipDataRef.current.anchor,
                position: { ...setTooltipPositionRef.current }
            };
        }
        setSetTooltipData({ set: null, anchor: null });
        setTooltipDataRef.current = { set: null, anchor: null };

        const stacked = !!options.stacked;

        setTooltip({
            item,
            anchor: event.currentTarget,
            interactive,
            stacked,
            onOpenItem: options.onOpenItem || null,
            slot: options.slot ?? null,
        });
        setTooltipPos(calculateTooltipPosition(rect, { stacked }));
    }, []);

    const handleTooltipHide = useCallback((event) => {
        // Crossing from the slot into its own tooltip is not leaving it.
        const entering = event?.relatedTarget;
        if (entering instanceof Node && tooltipElRef.current?.contains(entering)) return;

        setTooltip(NO_TOOLTIP);

        // Restore set tooltip if there was a pending one
        if (pendingSetTooltipRef.current.set) {
            const pending = pendingSetTooltipRef.current;
            setSetTooltipData({
                set: pending.set,
                anchor: pending.anchor
            });
            setTooltipDataRef.current = { set: pending.set, anchor: pending.anchor };
            setSetTooltipPosition(pending.position);
            setTooltipPositionRef.current = pending.position;
            pendingSetTooltipRef.current = { set: null, anchor: null, position: null };
        }
    }, []);

    // The mirror of the check in handleTooltipHide: leaving the tooltip back
    // towards its own slot keeps it up, because the slot is about to re-assert
    // it anyway and the flicker in between is what the user would see.
    const handleTooltipLeave = useCallback((event) => {
        const entering = event?.relatedTarget;
        if (entering instanceof Node && tooltip.anchor?.contains(entering)) return;
        handleTooltipHide();
    }, [tooltip.anchor, handleTooltipHide]);

    // Set tooltip handlers
    const handleSetTooltipShow = useCallback((set, event) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const position = calculateTooltipPosition(rect, {
            yOffset: 1,
            useRightEdge: true
        });

        // Only show set tooltip if no item tooltip is active
        if (tooltip.item) {
            // Store as pending to restore when item tooltip hides
            pendingSetTooltipRef.current = {
                set,
                anchor: event.currentTarget,
                position
            };
            return;
        }

        setSetTooltipData({ set, anchor: event.currentTarget });
        setTooltipDataRef.current = { set, anchor: event.currentTarget };
        setSetTooltipPosition(position);
        setTooltipPositionRef.current = position;
    }, [tooltip.item]);

    const handleSetTooltipHide = useCallback(() => {
        setSetTooltipData({ set: null, anchor: null });
        setTooltipDataRef.current = { set: null, anchor: null };
        pendingSetTooltipRef.current = { set: null, anchor: null, position: null };
    }, []);

    return {
        tooltip,
        tooltipPos,
        tooltipElRef,
        slotTooltipWeaponMaging,
        setTooltipData,
        setTooltipPosition,
        handleTooltipShow,
        handleTooltipHide,
        handleTooltipLeave,
        handleSetTooltipShow,
        handleSetTooltipHide,
    };
}
